import React, { useLayoutEffect } from 'react';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Checkbox,
	Notification,
	Box,
	Button,
	Flex,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { ErrorCode, useContent } from '@/providers/ContentProvider';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { AppPaths } from '@/configs/appPaths';
import { useMutation } from '@tanstack/react-query';
import { validatePassword } from '@/utils/utilities';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import APP_CONFIG from '@/configs/appConfig';
import styles from './Register.module.scss';

export type RegisterForm = {
	companyName: string;
	vatNumber: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	password: string;
	repeatPassword: string;
	terms: boolean;
	recaptchaToken?: string;
};

const initialValues: RegisterForm = {
	companyName: '',
	vatNumber: '',
	firstName: '',
	lastName: '',
	phoneNumber: '',
	email: '',
	password: '',
	repeatPassword: '',
	terms: false,
};

const baseSlug = 'register.';

export const Register: React.FC = () => {
	const { getErrorMessage, getContent, getMutationErrorMessage } = useContent();

	const { other } = useMantineTheme();

	useLayoutEffect(() => {
		document.body.classList.add('show-recaptcha');

		return () => {
			document.body.classList.remove('show-recaptcha');
		};
	}, []);

	const form = useForm({
		initialValues,
		validate: {
			email: isEmail(getErrorMessage([ErrorCode.INVALID_EMAIL])),
			password: (value, values) => {
				if (value !== values.repeatPassword)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
			repeatPassword: (value, values) => {
				if (value !== values.password)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
		},
	});

	const registerMutation = useMutation({
		mutationFn: UnauthenticatedApi.register,
		meta: {
			ignoreErrors: true,
		},
		onSuccess: () => {
			form.reset();
		},
	});

	const handleSubmit = form.onSubmit(async (values) => {
		const recaptchaToken = await window.grecaptcha.execute(
			APP_CONFIG.RECAPTCHA_KEY,
			{
				action: 'register',
			}
		);

		registerMutation.mutate({
			...values,
			recaptchaToken,
		});
	});

	return (
		<Box className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={registerMutation.isPending} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}title`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}description`)}
				</Text>
				{registerMutation.isSuccess && (
					<Notification
						{...other.notificationProps.success}
						mb="lg"
						onClose={registerMutation.reset}
					>
						<Text size="md">
							{getContent(`${baseSlug}notificationSuccess`)}
						</Text>
					</Notification>
				)}
				{registerMutation.isError && (
					<Notification
						{...other.notificationProps.danger}
						mb="lg"
						onClose={registerMutation.reset}
					>
						<Text size="md">
							{getMutationErrorMessage(registerMutation.error)}
						</Text>
					</Notification>
				)}
				<Flex mb="lg" gap="md">
					<TextInput
						{...form.getInputProps('firstName')}
						required
						placeholder={getContent(`namePlaceholder`)}
					/>
					<TextInput
						{...form.getInputProps('lastName')}
						required
						placeholder={getContent(`surnamePlaceholder`)}
					/>
				</Flex>
				<TextInput
					{...form.getInputProps('email')}
					required
					type="email"
					mb="lg"
					placeholder={getContent(`emailPlaceholder`)}
				/>
				<TextInput
					{...form.getInputProps('phoneNumber')}
					required
					inputMode="tel"
					type="tel"
					mb="lg"
					placeholder={getContent(`phonePlaceholder`)}
				/>
				<TextInput
					{...form.getInputProps('companyName')}
					required
					mb="lg"
					placeholder={getContent(`companyPlaceholder`)}
				/>
				<TextInput
					{...form.getInputProps('vatNumber')}
					required
					mb="lg"
					placeholder={getContent(`taxNumberPlaceholder`)}
					maxLength={10}
					minLength={10}
				/>
				<PasswordInput
					{...form.getInputProps('password')}
					required
					mb="lg"
					placeholder={getContent(`passwordPlaceholder`)}
				/>
				<PasswordInput
					{...form.getInputProps('repeatPassword')}
					required
					mb="lg"
					placeholder={getContent(`repeatPasswordPlaceholder`)}
				/>
				<Checkbox
					{...form.getInputProps('terms', { type: 'checkbox' })}
					required
					mb="lg"
					label={getContent(`statuteCheckbox`)}
				/>
				<Button type="submit" mb="lg" w="100%">
					{getContent(`${baseSlug}submitButton`)}
				</Button>
				<Box ta="center" c="dimmed" mb="md">
					{getContent(`${baseSlug}goBackToLogin`, {
						links: {
							$LOGIN: {
								to: `/${AppPaths.LOGIN}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</Box>
	);
};
